import React,{useState,useEffect} from 'react';
import { Typography, Button, createMuiTheme, ThemeProvider, CssBaseline, Container, colors, makeStyles, createStyles, Theme, Box } from '@material-ui/core';
import { Layout } from './views/Layout';
import { blue, orange, red } from '@material-ui/core/colors';
import { KeyboardArrowDownRounded } from '@material-ui/icons';
import {
  BrowserRouter as Router,
  Switch,
  Route,
  useHistory,
} from "react-router-dom";
import { Home } from './views/Home';
import { Faq } from './views/Faq';
import { ApolloProvider } from '@apollo/client';
import { httpApolloClient } from './httpApolloClient';
import { Leaderboard } from './views/Season/Leaderboard';
import TournamentContainer from './views/Tournament/TournamentPages';
import EventsContainer from './views/Events/EventsPages';
import { FeatPlanner } from './views/Feats/FeatPlanner';
import MapsPage from './views/Maps/MapsPage';
import { CharacterContainer } from 'views/Character/CharacterContainer';
import GameContainer from 'views/Game/GameContainer';
import { MatchmakingRules } from 'views/Matchmaking/MatchmakingRules';
import { DuelLeaderboard } from 'views/Season/DuelLeaderboard';
import { OpenWorldContainer } from 'views/OpenWorld/OpenWorldContainer';
import { DuelContainer } from 'views/Duel/DuelContainer';
import { UserContainer } from 'views/User/UserContainer';
import { PveContainer } from 'views/Pve/PveContainer';



function Copyright() {
  return (
    <Typography variant="body2" color="textSecondary" align="center">
      {'Copyright © Tim '}
      {new Date().getFullYear()}
    </Typography>
  );
}

function App() {
  const theme = createMuiTheme({
    palette: {
      type:'dark',
      background:{
        default: "#282b32",
        paper: "#3C3E48"
      },
      divider: "#484E5B",
      primary: {
        main: blue[800]
      },
      secondary: orange,
      error: red
    },
    props: {
      MuiSelect: {
        IconComponent: KeyboardArrowDownRounded,
      },
      // every dropdown opens just below its field instead of on top of it
      MuiMenu: {
        getContentAnchorEl: null,
        anchorOrigin: {vertical: 'bottom', horizontal: 'left'},
        transformOrigin: {vertical: 'top', horizontal: 'left'},
      },
    },
    overrides: {
      MuiSelect: {
        select: {
          borderRadius: 6,
          transition: 'background-color 0.15s ease',
          '&:hover': {
            backgroundColor: 'rgba(255, 255, 255, 0.04)',
          },
          '&:focus': {
            backgroundColor: 'rgba(255, 255, 255, 0.04)',
            borderRadius: 6,
          },
        },
        icon: {
          color: 'rgba(255, 255, 255, 0.6)',
          transition: 'transform 0.2s ease',
        },
      },
      MuiMenu: {
        paper: {
          marginTop: 6,
          borderRadius: 10,
          backgroundColor: '#2b2e36',
          border: '1px solid rgba(255, 255, 255, 0.08)',
          boxShadow: '0 14px 36px rgba(0, 0, 0, 0.5), 0 2px 6px rgba(0, 0, 0, 0.3)',
        },
        list: {
          padding: 6,
        },
      },
      // Every dialog: a dark card with a bold title, softer body text and rounded action buttons (dialogs with their own
      // paper class, e.g. Best Teams or Play As, override parts of this)
      MuiDialog: {
        root: {
          '& .MuiBackdrop-root': {
            backgroundColor: 'rgba(8, 9, 12, 0.7)',
            backdropFilter: 'blur(3px)',
          },
        },
        paper: {
          borderRadius: 12,
          background: 'linear-gradient(180deg, #30333b, #26282f)',
          border: '1px solid rgba(255, 255, 255, 0.08)',
          borderTop: '2px solid #1e88e5',
          boxShadow: '0 22px 50px rgba(0, 0, 0, 0.55), 0 2px 8px rgba(0, 0, 0, 0.3)',
        },
        paperWidthSm: {
          minWidth: 380,
        },
      },
      MuiDialogTitle: {
        root: {
          padding: '20px 24px 8px',
          '& h2': {
            fontSize: '1.15rem',
            fontWeight: 800,
            letterSpacing: '0.01em',
            lineHeight: 1.35,
          },
        },
      },
      MuiDialogContent: {
        root: {
          padding: '6px 24px 14px',
          fontSize: '0.95rem',
          lineHeight: 1.55,
          color: 'rgba(255, 255, 255, 0.82)',
          '&:first-child': {
            paddingTop: 22,
          },
        },
        dividers: {
          padding: '14px 24px',
          borderColor: 'rgba(255, 255, 255, 0.07)',
        },
      },
      MuiDialogContentText: {
        root: {
          color: 'rgba(255, 255, 255, 0.68)',
        },
      },
      MuiDialogActions: {
        root: {
          padding: '10px 24px 20px',
          '& .MuiButton-root': {
            textTransform: 'none',
            fontWeight: 700,
            borderRadius: 8,
            padding: '6px 16px',
            boxShadow: 'none',
          },
          '& .MuiButton-containedPrimary': {
            background: 'linear-gradient(135deg, #1e88e5, #1565c0)',
            boxShadow: '0 4px 14px rgba(30, 136, 229, 0.3)',
            '&:hover': {
              background: 'linear-gradient(135deg, #2196f3, #1976d2)',
            },
          },
          // "default" contained buttons (Cancel, No, Close) read as the quiet option
          '& .MuiButton-contained:not(.MuiButton-containedPrimary):not(.MuiButton-containedSecondary)': {
            color: 'rgba(255, 255, 255, 0.85)',
            backgroundColor: 'rgba(255, 255, 255, 0.08)',
            '&:hover': {
              backgroundColor: 'rgba(255, 255, 255, 0.14)',
            },
          },
          '& .MuiButton-root.Mui-disabled': {
            background: 'rgba(255, 255, 255, 0.06)',
            boxShadow: 'none',
          },
        },
      },
      MuiMenuItem: {
        root: {
          borderRadius: 6,
          margin: '1px 0',
          minHeight: 36,
          fontSize: '0.92rem',
          transition: 'background-color 0.12s ease',
          '&:hover': {
            backgroundColor: 'rgba(255, 255, 255, 0.07)',
          },
          '&$selected': {
            backgroundColor: 'rgba(66, 165, 245, 0.16)',
            boxShadow: 'inset 3px 0 0 #42a5f5',
            color: '#bbdefb',
            fontWeight: 600,
          },
          '&$selected:hover': {
            backgroundColor: 'rgba(66, 165, 245, 0.24)',
          },
        },
      },
    },
  })
  
  return (
    <ApolloProvider client={httpApolloClient}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <Layout>
          <Switch>
            <Route path="/faq" component={Faq} />
            <Route path="/leaderboard" component={Leaderboard} />
            <Route path="/duelleaderboard" component={DuelLeaderboard} />
            <Route path="/characters" component={CharacterContainer} />
            <Route path="/game" component={GameContainer} />
            <Route path="/duel" component={DuelContainer} />
            <Route path="/rules" component={MatchmakingRules} />
            <Route path="/users" component={UserContainer} />
            <Route path="/pve" component={PveContainer} />
            <Route path="/tournaments" component={TournamentContainer} />
            <Route path="/events" component={EventsContainer} />
            <Route path="/feats" component={FeatPlanner} />
            <Route path="/maps" component={MapsPage} />
            <Route path="/" component={Home} />
          </Switch>
        </Layout>
      </ThemeProvider>
    </ApolloProvider>
    
  );
}

export default App;
